import * as React from 'react';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import { __t, applyTranslations } from 'utils/language';
import { isRequired, isUrl, isZalandoPage, maxLength } from 'utils/validators';
import {
  DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
  DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION
} from 'utils/deliverables';

import './bulk-wmc-generator-form.scss';

applyTranslations(require('./bulk-wmc-generator-form.i18n.json'));

import type { TextEntry } from 'types';
import type {
  BulkEditZalandoLinkFieldType,
  BulkEditZalandoLinkWmcRequest
} from 'modules/subcampaigns/types/BulkOfferModal.type';

type Props = {
  actionType: string,
  isMultiZLinkEnabled: boolean,
  onChange: (wmcRequest: BulkEditZalandoLinkWmcRequest) => void
};

type State = BulkEditZalandoLinkWmcRequest;

const MSG_KEY = 'bulk_wmc_generator_form';
const MAX_NAME_LENGTH = 25;
const MAX_DESCRIPTION_LENGTH = 80;

class BulkWMCGeneratorForm extends React.Component<Props, State> {
  state = {
    primary: {
      url: '',
      name: '',
      description: ''
    },
    secondary: {
      url: '',
      name: '',
      description: ''
    }
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const { actionType } = this.props;
    const { primary, secondary } = this.state;

    return actionType !== nextProps.actionType
      || primary.url !== nextState.primary.url
      || primary.name !== nextState.primary.name
      || primary.description !== nextState.primary.description
      || secondary.url !== nextState.secondary.url
      || secondary.name !== nextState.secondary.name
      || secondary.description !== nextState.secondary.description;
  }

  onWMCRequestChange = () => {
    const { primary, secondary } = this.state;

    this.props.onChange({
      primary,
      secondary
    });
  };

  handleChange = (
    event: React.SyntheticEvent<TextEntry>
  ): void => {
    const target = event.currentTarget;
    const type = String(target.dataset.type);
    const name = String(target.dataset.name);
    const value = String(target.value);

    this.setState(state => ({
      ...state,
      [type]: {
        ...state[type],
        [name]: value
      }
    }), this.onWMCRequestChange);
  };

  renderDescriptionField = (
    type: BulkEditZalandoLinkFieldType
  ): React.ReactNode => {
    const key = this.state[type];

    return (
      <div className="bulk-wmc-form__description">
        <FormField
          label={__t(`${MSG_KEY}.labels.zalando_landing_page_description`)}
          labelType={type === 'primary' ? 'highlighted' : 'normal'}
          showValidationIcon={false}
          fontSize="small"
          validators={[
            maxLength(key.description, MAX_DESCRIPTION_LENGTH)
          ]}
        >
          <Input
            value={key.description}
            width="responsive"
            placeholder={__t(`${MSG_KEY}.placeholders.landing_page_description`)}
            charCount={{
              curCount: key.description.length,
              maxChars: MAX_DESCRIPTION_LENGTH
            }}
            onChange={this.handleChange}
            data={{ type, name: 'description' }}
          />
        </FormField>
      </div>
    );
  };

  renderNameField = (
    type: BulkEditZalandoLinkFieldType
  ): React.ReactNode => {
    const key = this.state[type];

    return (
      <div className="bulk-wmc-form__name">
        <FormField
          showRequiredMark
          label={__t(`${MSG_KEY}.labels.zalando_landing_page_name`)}
          labelType={type === 'primary' ? 'highlighted' : 'normal'}
          showValidationIcon={false}
          fontSize="small"
          validators={[
            isRequired(key.name, __t(`${MSG_KEY}.errors.name`)),
            maxLength(key.name, MAX_NAME_LENGTH)
          ]}
        >
          <Input
            value={key.name}
            width="responsive"
            placeholder={__t(`${MSG_KEY}.placeholders.landing_page_name`)}
            charCount={{
              curCount: key.name.length,
              maxChars: MAX_NAME_LENGTH
            }}
            onChange={this.handleChange}
            data={{ type, name: 'name' }}
          />
        </FormField>
      </div>
    );
  };

  renderUrlField = (
    type: BulkEditZalandoLinkFieldType,
    linkType?: 'existing' | 'new'
  ): React.ReactNode => {
    const key = this.state[type];

    return (
      <div className="bulk-wmc-form__url">
        <FormField
          showRequiredMark
          label={__t(`${MSG_KEY}.labels.zalando_landing_page${linkType ? `_${linkType}` : ''}`)}
          labelType={type === 'primary' ? 'highlighted' : 'normal'}
          showValidationIcon={false}
          fontSize="small"
          validators={[
            isRequired(key.url, __t(`${MSG_KEY}.errors.url`)),
            isUrl(key.url),
            isZalandoPage(key.url)
          ]}
        >
          <Input
            value={key.url}
            width="responsive"
            placeholder={__t(`${MSG_KEY}.placeholders.landing_page_url${linkType ? `_${linkType}` : ''}`)}
            onChange={this.handleChange}
            data={{ type, name: 'url' }}
          />
        </FormField>
      </div>
    );
  };

  render() {
    const { actionType, isMultiZLinkEnabled } = this.props;

    const isAddAction = actionType === DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION
      || actionType === DELIVERABLE_ADD_ZALANDO_PAGE_ACTION;

    const isReplaceAction = actionType === DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION;

    return (
      <div className="bulk-wmc-form">
        {
          isAddAction && (
            <React.Fragment>
              {this.renderUrlField('primary')}
              {
                isMultiZLinkEnabled &&
                  this.renderNameField('primary')
              }
              {this.renderDescriptionField('primary')}
            </React.Fragment>
          )
        }
        {
          isReplaceAction && (
            <React.Fragment>
              {this.renderUrlField('primary', 'existing')}
              <div className="bulk-wmc-form__section-title">
                {__t(`${MSG_KEY}.sections.enter_new_tracking_link_info`)}
              </div>
              {this.renderUrlField('secondary', 'new')}
              {this.renderNameField('secondary')}
              {this.renderDescriptionField('secondary')}
            </React.Fragment>
          )
        }
      </div>
    );
  }
}

export default BulkWMCGeneratorForm;
